import { useEffect } from 'react';

/**
 * Custom hook for managing document title and meta tags
 * Works around react-helmet-async issues with React 19
 */
export function usePageTitle(title) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }
    // Cleanup: restore default title on unmount (optional)
    return () => {
      document.title = 'Mini Stripes UK | Premium Mini Cooper Graphics';
    };
  }, [title]);
}

export function usePageMeta({ 
  title, 
  description, 
  keywords, 
  canonical, 
  ogTitle, 
  ogDescription, 
  ogImage, 
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData 
}) {
  useEffect(() => {
    // Set title
    if (title) {
      document.title = title;
    }

    // Helper to set or create meta tag
    const setMeta = (name, content, property = false) => {
      if (!content) return null;
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
      return meta;
    };

    // Set description
    const descMeta = setMeta('description', description);
    
    // Set keywords
    const keywordsMeta = setMeta('keywords', keywords);

    // Set canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    // Open Graph tags
    const ogTitleMeta = setMeta('og:title', ogTitle || title, true);
    const ogDescMeta = setMeta('og:description', ogDescription || description, true);
    const ogImageMeta = setMeta('og:image', ogImage, true);
    const ogTypeMeta = setMeta('og:type', ogType, true);
    const ogUrlMeta = setMeta('og:url', window.location.href, true);
    const ogSiteMeta = setMeta('og:site_name', 'Mini Stripes UK', true);

    // Twitter Card tags
    const twitterCardMeta = setMeta('twitter:card', twitterCard);
    const twitterTitleMeta = setMeta('twitter:title', ogTitle || title);
    const twitterDescMeta = setMeta('twitter:description', ogDescription || description);
    const twitterImageMeta = setMeta('twitter:image', ogImage);

    // Structured data (JSON-LD)
    let ldScript = document.querySelector('script[type="application/ld+json"]');
    if (structuredData) {
      if (!ldScript) {
        ldScript = document.createElement('script');
        ldScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(ldScript);
      }
      ldScript.textContent = JSON.stringify(structuredData);
    }

    // Cleanup on unmount
    return () => {
      document.title = 'Mini Stripes UK | Premium Mini Cooper Graphics';
      // Note: We don't remove meta tags on unmount as they should be replaced by the next page
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, twitterCard, structuredData]);
}

export default usePageMeta;
